import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import { ThemeProvider } from 'react-jss';
import JssProvider from 'react-jss/lib/JssProvider';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';

import {
  ErrorBoundary,
  FastWorkflowOverviewWithContext,
  GdprPolicy,
  GroupBoard, // Kanban view removed
  GroupSettings,
  NewLink,
  OAuth,
  PageNotFound,
  PageUnauthorized,
  TraceInspector,
  UnexpectedErrorBoundary,
  UserDashboard,
  WorkflowExport,
  WorkflowGroups,
  // WorkflowBoard, // Kanban view removed
  WorkflowOverview
} from '@/components';
import { BatchActionHistoryPage } from '@/components/batchActionHistory/batchActionHistoryPage';
import { env } from '@/constant/env';
import * as routes from '@/constant/routes';
import { SENTRY_DSN, SENTRY_TUNNEL } from '@/constant/sentry';
import { traceClient } from '@/gql';
import theme from '@/style';
import '@/style/style.css';

import { ShadcnThemeProvider } from '@/shadcn/theme/theme-provider';

import { Login } from '@/components/login/login';
import { RouteWorkspaceBorder } from '@/components/RouteWorkspaceBorder';
import { SandboxPage } from '@/components/sandbox/sandboxPage';
import { CustomToaster } from '@/components/toast';
import { ReAuthProvider } from '@/contexts/reAuth.context';
import generateClassName from '@/utils/generateClassName';
import { PrivateRoute } from './privateRoute';

export const history = createBrowserHistory();

Sentry.init({
  dsn: SENTRY_DSN,
  tunnel: SENTRY_TUNNEL,
  environment: env.VITE_ENVIRONMENT,
  debug: env.VITE_SENTRY_DEBUG,

  integrations: [
    Sentry.reactRouterV5BrowserTracingIntegration({
      history
    }),
    Sentry.replayIntegration({ unmask: ['.sentry-unmask'] }),
    Sentry.graphqlClientIntegration({
      endpoints: [/\/graphql/]
    })
  ],
  enabled: env.VITE_ENVIRONMENT !== 'local',
  ignoreErrors: [
    'ResizeObserver loop completed with undelivered notifications'
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  tracesSampleRate: 1,
  sampleRate: 1.0,
  profilesSampleRate: 0.1,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['stratumn.com'],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1
});

export const Root = () => (
  <ApolloProvider client={traceClient}>
    <ShadcnThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <ThemeProvider theme={theme}>
        <JssProvider generateClassName={generateClassName}>
          <UnexpectedErrorBoundary>
            <Router history={history}>
              <ReAuthProvider>
                <QueryParamProvider adapter={ReactRouter5Adapter}>
                  {/* <Snackbars> */}
                  <RouteWorkspaceBorder />
                  <ErrorBoundary>
                    <Switch>
                      <Route
                        component={Login}
                        exact
                        path={routes.ROUTE_LOGIN}
                      />
                      <Route
                        component={OAuth}
                        exact
                        path={routes.ROUTE_OAUTH}
                      />

                      <PrivateRoute
                        component={UserDashboard}
                        exact
                        path={routes.ROUTE_WORKFLOW_DASHBOARD}
                      />
                      {/* Kanban view removed */}
                      {/* <PrivateRoute
                      path={routes.ROUTE_WORKFLOW_BOARD}
                      exact
                      component={WorkflowBoard}
                    /> */}
                      {/* New fast workflow overview */}
                      <PrivateRoute
                        component={FastWorkflowOverviewWithContext}
                        exact
                        path={routes.NEW_WF_OVERVIEW_ROUTE}
                      />
                      <PrivateRoute
                        // @ts-expect-error
                        component={WorkflowOverview}
                        exact
                        path={routes.OLD_WF_OVERVIEW_ROUTE}
                      />
                      {env.FEATURE_FLAGS.USE_SANDBOX && (
                        <PrivateRoute
                          component={SandboxPage}
                          exact
                          path={routes.ROUTE_SANDBOX}
                        />
                      )}
                      <PrivateRoute
                        component={WorkflowExport}
                        exact
                        path={routes.ROUTE_WORKFLOW_EXPORT}
                      />
                      {env.FEATURE_FLAGS.USE_BATCH_ACTION && (
                        <PrivateRoute
                          component={BatchActionHistoryPage}
                          exact
                          path={routes.ROUTE_WORKFLOW_BATCH_ACTIONS}
                        />
                      )}
                      {/* Kanban view removed */}
                      <PrivateRoute
                        component={GroupBoard}
                        exact
                        path={routes.ROUTE_GROUP_BOARD}
                      />
                      <PrivateRoute
                        component={NewLink}
                        exact
                        path={routes.ROUTE_NEW_LINK}
                      />
                      <PrivateRoute
                        component={TraceInspector}
                        exact
                        path={[
                          routes.ROUTE_INSPECT_TRACE,
                          routes.ROUTE_INSPECT_TRACE_LINK
                        ]}
                      />
                      <PrivateRoute exact path={routes.ROUTE_USER_DASHBOARD}>
                        <Redirect to={routes.ROUTE_WORKFLOW_DASHBOARD} />
                      </PrivateRoute>
                      <PrivateRoute
                        component={WorkflowGroups}
                        exact
                        path={routes.ROUTE_WORKFLOW_GROUPS}
                      />
                      <PrivateRoute
                        component={GroupSettings}
                        exact
                        path={routes.ROUTE_GROUP_SETTINGS}
                      />
                      {/* accessible by all environments except release */}
                      <PrivateRoute
                        component={PageUnauthorized}
                        exact
                        path={routes.ROUTE_UNAUTHORIZED}
                      />
                      <PrivateRoute
                        component={GdprPolicy}
                        exact
                        path={routes.ROUTE_GDPR_POLICY}
                      />
                      <PrivateRoute // @ts-expect-error
                        component={PageNotFound}
                        path="*"
                      />
                    </Switch>
                  </ErrorBoundary>
                </QueryParamProvider>
              </ReAuthProvider>
            </Router>
            <CustomToaster />
          </UnexpectedErrorBoundary>
        </JssProvider>
      </ThemeProvider>
    </ShadcnThemeProvider>
  </ApolloProvider>
);
